import React from 'react';
import { ArrowRight, ChatCircleDots, MapPin, UsersThree } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import PublicLayout from './PublicLayout';
import { useSeoHead } from './SeoHead';

const appSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'mapbl',
  url: 'https://mapbl.stream/',
  applicationCategory: 'SocialNetworkingApplication',
  operatingSystem: 'Any',
  description: 'A live, browser-based social map for sharing statuses and discovering people and moments nearby.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
};

const LandingPage: React.FC = () => {
  useSeoHead({
    title: 'mapbl – A real-time social map for nearby moments',
    description: 'Share map-based statuses, discover nearby people, and see live moments unfold on mapbl—the browser-based social map.',
    canonicalPath: '/',
    structuredData: appSchema,
  });

  return (
    <PublicLayout>
      <main>
        <section className="relative overflow-hidden px-5 py-20 sm:py-28">
          <div aria-hidden="true" className="absolute left-1/4 top-0 h-80 w-80 rounded-full bg-cyan-400/15 blur-3xl" />
          <div aria-hidden="true" className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-violet-500/15 blur-3xl" />
          <div className="relative mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <p className="mb-5 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">People make the map</p>
              <h1 className="max-w-3xl text-5xl font-semibold leading-[1.04] tracking-[-0.045em] sm:text-7xl">A live social map for the moments around you.</h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300">Share what is happening, discover nearby statuses, and meet the community through a map that changes in real time.</p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-violet-500 px-6 py-3.5 font-semibold shadow-xl shadow-cyan-500/20" to="/register">Join mapbl <ArrowRight size={18} weight="bold" /></Link>
                <Link className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-6 py-3.5 font-medium text-cyan-100 transition hover:border-cyan-300/60" to="/app">Explore as guest</Link>
                <Link className="rounded-full border border-white/15 px-6 py-3.5 font-medium text-slate-200 transition hover:border-cyan-400" to="/how-it-works">See how it works</Link>
              </div>
            </div>
            <div className="relative mx-auto aspect-square w-full max-w-lg">
              <div aria-hidden="true" className="absolute inset-[8%] rounded-full bg-cyan-400/15 blur-3xl" />
              <img
                alt="Glowing social connections across a digital globe"
                className="relative h-full w-full object-contain drop-shadow-[0_0_50px_rgba(34,211,238,0.2)] [mask-image:radial-gradient(circle_at_center,black_58%,transparent_78%)]"
                decoding="async"
                src={`${process.env.PUBLIC_URL}/landing-social-globe-transparent.png`}
              />
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-white/[0.025] px-5 py-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">The map is the feed.</h2>
            <p className="mt-3 max-w-2xl text-slate-400">No endless timeline. Explore people and public stories in their geographic context.</p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                [MapPin, 'Choose your place', 'Use your browser location or put yourself anywhere on the map.'],
                [ChatCircleDots, 'Share a live status', 'Post a message, event, announcement, or party tied to a place.'],
                [UsersThree, 'Discover the community', 'Explore public profiles, nearby stories, reactions, and conversations.'],
              ].map(([Icon, title, text]) => {
                const FeatureIcon = Icon as React.ElementType;
                return <article key={String(title)} className="rounded-[28px] border border-white/10 bg-white/[0.04] p-6"><FeatureIcon size={28} className="text-cyan-300" /><h3 className="mt-5 text-xl font-semibold">{String(title)}</h3><p className="mt-2 leading-7 text-slate-400">{String(text)}</p></article>;
              })}
            </div>
          </div>
        </section>
      </main>
    </PublicLayout>
  );
};

export default LandingPage;
